import { memo, useCallback, useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { useNavigate } from "react-router-dom";

import { deleteDatabase, openDatabase } from "../../../../Utils/db";

import WorkspaceService from "../../../../infrastructure/Services/WorkspaceService";

import authService from "../../../../services/authService";

import { Avatar, Item, Items, Menu, Root, UserName } from "./Profile.styles";

interface ProfileProps {
  account: string | null | undefined;

  showSettings?: boolean;
}

const Profile = ({ account, showSettings = true }: ProfileProps) => {
  const navigate = useNavigate();

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [userEmail, setUserEmail] = useState<string | null>(null);

  const [userAvatar, setUserAvatar] = useState<string | null>(null);

  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!account) return;

    let cancelled = false;

    authService

      .getMe()

      .then((profile) => {
        if (cancelled) return;

        setUserEmail(profile.email || profile.name || null);

        setUserAvatar(profile.avatar || null);
      })

      .catch(async () => {
        if (cancelled) return;

        try {
          const db = await openDatabase();

          const user = await db.user.where("account").equals(account).first();

          if (!cancelled && user?.email) {
            setUserEmail(user.email);
          }
        } catch {
          // ignore DB errors
        }
      });

    return () => {
      cancelled = true;
    };
  }, [account]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileRef.current &&
        !profileRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  const { t } = useTranslation("dashboard");

  const handleDisconnect = useCallback(async () => {
    try {
      try {
        await authService.logout();
      } catch (error) {
        console.warn("Could not notify backend logout:", error);
      }

      localStorage.removeItem("@Eriol/system/userId");

      localStorage.removeItem("@Eriol/system/accessKey");

      localStorage.removeItem("lastAuthProvider");

      localStorage.removeItem("@Eriol/currentWorkspaceId");

      window.dispatchEvent(new Event("auth-changed"));

      // Resetear el scope per-user en la extensión (currentUserId → null),

      // igual que SessionExpiredModal. Sin esto el logout voluntario dejaba el

      // scope apuntando al usuario anterior. (→ IPC "auth-logout".)

      try {
        await window.electronAPI?.logout?.();
      } catch (error) {
        console.warn("Could not reset extension session on logout:", error);
      }

      try {
        const workspaceService = new WorkspaceService("api");

        await workspaceService.unselect();
      } catch (error) {
        console.warn("Could not unselect workspaces:", error);
      }

      await deleteDatabase();

      navigate("/");
    } catch (error) {
      console.error("Error during disconnect:", error);

      navigate("/");
    }
  }, [navigate]);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen((prev) => !prev);
  }, []);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const displayName = userEmail || "";

  return (
    <Root ref={profileRef}>
      {displayName && <UserName onClick={toggleMenu}>{displayName}</UserName>}

      <Avatar onClick={toggleMenu} data-image={userAvatar ? "true" : undefined}>
        {userAvatar ? (
          <img
            src={userAvatar}
            alt="Profile"
            onError={() => setUserAvatar(null)}
          />
        ) : null}
      </Avatar>

      {isMenuOpen && (
        <Menu>
          <Items>
            {showSettings && (
              <Item
                onClick={() => {
                  navigate("/settings");

                  closeMenu();
                }}
              >
                {t("DASHBOARD.TOOLBAR.PROFILE.ITEMS.settings")}
              </Item>
            )}
            <Item
              onClick={() => {
                handleDisconnect();

                closeMenu();
              }}
            >
              {t("DASHBOARD.TOOLBAR.PROFILE.ITEMS.logout")}
            </Item>
          </Items>
        </Menu>
      )}
    </Root>
  );
};

export default memo(Profile);
